import moment from 'moment-timezone';

/** Must match backend masterConfig.BUSINESS_TIMEZONE default. */
export const BUSINESS_TIMEZONE = 'America/Jamaica';

export function businessNow() {
    return moment.tz(BUSINESS_TIMEZONE);
}

export function businessToday() {
    return businessNow().format('YYYY-MM-DD');
}

function padYmd(year, month, day) {
    return `${year}-${String(Number(month)).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`;
}

/** Exact UTC midnight is a stored calendar day (not 7pm the previous evening). */
export function isUtcDateOnlyInstant(date) {
    if (date == null || date === '') return false;
    if (typeof date === 'string') {
        return /T00:00:00(?:\.000)?Z$/.test(date.trim());
    }
    const d = date instanceof Date ? date : (moment.isMoment(date) ? date.toDate() : new Date(date));
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return false;
    return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
}

/**
 * Civil day. Plain Y-M-D and UTC-midnight instants keep that calendar date.
 * Other instants use America/Jamaica.
 */
export function businessDateOnly(date) {
    if (date == null || date === '') return businessToday();
    if (typeof date === 'string') {
        const trimmed = date.trim();
        const plain = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (plain) return padYmd(plain[1], plain[2], plain[3]);
        if (isUtcDateOnlyInstant(trimmed)) {
            const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})T/);
            if (iso) return padYmd(iso[1], iso[2], iso[3]);
        }
    }
    const asDate = date instanceof Date ? date : (moment.isMoment(date) ? date.toDate() : new Date(date));
    if (asDate instanceof Date && !Number.isNaN(asDate.getTime()) && isUtcDateOnlyInstant(asDate)) {
        return padYmd(asDate.getUTCFullYear(), asDate.getUTCMonth() + 1, asDate.getUTCDate());
    }
    return moment.tz(date, BUSINESS_TIMEZONE).format('YYYY-MM-DD');
}

/** Start of the civil day in Jamaica. UTC midnight stays on that calendar day. */
export function businessStartOfDay(date) {
    if (date == null || date === '') {
        return moment.tz(BUSINESS_TIMEZONE).startOf('day');
    }
    return moment.tz(businessDateOnly(date), 'YYYY-MM-DD', BUSINESS_TIMEZONE).startOf('day');
}

export function businessEndOfDay(date) {
    return businessStartOfDay(date).clone().endOf('day');
}

/** Format a civil day for display without the browser timezone shifting it. */
export function formatBusinessDate(date, format) {
    if (date == null || date === '') return '';
    const pattern = format || 'MMM D, YYYY';
    return moment.tz(businessDateOnly(date), 'YYYY-MM-DD', BUSINESS_TIMEZONE).format(pattern);
}

/** Whole days from a to b (b - a) on the business calendar. */
export function businessDiffDays(a, b) {
    return businessStartOfDay(b).diff(businessStartOfDay(a), 'days');
}

/** ISO instant for end of a business calendar day (tag expiry etc.). */
export function businessEndOfDayIso(dateStr) {
    if (!dateStr) return null;
    const m = businessEndOfDay(dateStr);
    if (!m.isValid()) return null;
    return m.toISOString();
}

/** ISO instant for noon on a business calendar day. */
export function businessNoonIso(dateStr) {
    if (!dateStr) return null;
    const m = businessStartOfDay(dateStr).hour(12).minute(0).second(0).millisecond(0);
    if (!m.isValid()) return null;
    return m.toISOString();
}
